import { OutlookApi } from './OutlookApi';
import { AttachmentOffice } from './AttachmentOffice';
import { DictionaryInterface } from 'titus-ts/dist/js/Common/Util/DictionaryInterface';
import { defaultHeaderFooterOptions, HeaderFooterOptions } from './HeaderFooterOptions';
import { Recipient, EmailUser } from 'titus-ts/dist/js/Mail/Recipient';
import { PlatformType } from './PlatformType';
import { WordApi } from './WordApi';
import { ExcelApi } from './ExcelApi';
import { AddinApi } from './AddinApi';
import { PowerPointApi } from './PowerPointApi';
import { MetadataObject } from 'titus-ts/dist/js/Util/Metadata/MetadataObject';
import { HostType } from './HostType';
import BuiltInProperties from 'titus-ts/dist/js/Office/BuiltInProperties';
import DocumentTypeEnum from 'titus-ts/dist/js/Office/DocumentTypeEnum';

export class OfficeApi {
    private outlookApi: OutlookApi;
    private itemId: string | undefined;
    private documentApi: WordApi | ExcelApi | PowerPointApi | undefined;
    private wordApi: WordApi;
    private excelApi: ExcelApi;
    private powerPointApi: PowerPointApi;
    private addinApi: AddinApi;
    private host: HostType | undefined;

    constructor(
        outlookApi?: OutlookApi,
        wordApi?: WordApi,
        excelApi?: ExcelApi,
        powerPointApi?: PowerPointApi,
        addinApi?: AddinApi,
    ) {
        this.outlookApi = outlookApi ? outlookApi : new OutlookApi();
        this.excelApi = excelApi ? excelApi : new ExcelApi();
        this.wordApi = wordApi ? wordApi : new WordApi();
        this.powerPointApi = powerPointApi ? powerPointApi : new PowerPointApi();
        this.addinApi = addinApi ? addinApi : new AddinApi();
    }

    public initialise() {
        this.outlookApi.initialise();
    }

    public getHost = (): HostType => {
        if (!this.host) {
            this.host = this.addinApi.getHost();
        }
        return this.host;
    };

    public setInternetHeadersAsync = async (headers: DictionaryInterface<string>): Promise<void> => {
        if (!this.itemId) {
            await this.saveAsync();
        }
        if (this.itemId) {
            await this.outlookApi.setInternetHeaders(this.itemId, headers);
        }
    };

    public getPrimayBackgroundColor = (): string => {
        return this.addinApi.getthemeColor();
    };

    public getInternetHeadersAsync = async (headersToRequest: string[]): Promise<DictionaryInterface<string>> => {
        this.itemId = this.outlookApi.getItemId();
        if (!this.itemId) {
            await this.saveAsync();
        }
        let result: DictionaryInterface<string> = {};
        if (this.itemId) {
            result = await this.outlookApi.getInternetHeaders(this.itemId, headersToRequest).catch(() => {
                console.log('Could not retrieve internet headers from message');
                return {};
            });
        }

        return result;
    };

    public getOriginalItemInternetHeaders = async (
        inReplyToInternetId: string,
        headersToRequest: string[],
    ): Promise<DictionaryInterface<string>> => {
        let result: DictionaryInterface<string> = {};

        if (!this.itemId) {
            await this.saveAsync();
        }

        if (this.itemId) {
            result = await this.outlookApi
                .getOriginalItemInternetHeaders(inReplyToInternetId, this.itemId, headersToRequest)
                .catch(() => {
                    console.log('Could not retrieve internet headers from inReplyTo message');
                    return {};
                });
        }
        return result;
    };

    public getToAsync = async (): Promise<Recipient[]> => {
        const result = await this.outlookApi.getToAsync();
        return result;
    };

    public getCcAsync = async (): Promise<Recipient[]> => {
        const result = await this.outlookApi.getCcAsync();
        return result;
    };

    public getBccAsync = async (): Promise<Recipient[]> => {
        const result = await this.outlookApi.getBccAsync();
        return result;
    };

    public setToAsync = async (emails: string[]): Promise<void> => {
        await this.outlookApi.setToAsync(emails);
    };

    public setCcAsync = async (emails: string[]): Promise<void> => {
        await this.outlookApi.setCcAsync(emails);
    };

    public setBccAsync = async (emails: string[]): Promise<void> => {
        await this.outlookApi.setBccAsync(emails);
    };

    public addToAsync = async (emails: string[]): Promise<void> => {
        await this.outlookApi.addToAsync(emails);
    };

    public addCcAsync = async (emails: string[]): Promise<void> => {
        await this.outlookApi.addCcAsync(emails);
    };

    public addBccAsync = async (emails: string[]): Promise<void> => {
        await this.outlookApi.addBccAsync(emails);
    };

    public saveAsync = async (): Promise<string> => {
        let result = '';
        if (this.getHost() === 'Outlook') {
            result = await this.outlookApi.saveAsync();
            this.itemId = result;
        }
        return result;
    };

    public getConversationId = (): string => {
        return this.outlookApi.getConversationId();
    };

    public getInReplyTo = (): string => {
        return this.outlookApi.getInReplyTo();
    };

    public getInReplyToAsync = async (): Promise<string> => {
        return await this.outlookApi.getInReplyToAsync();
    };

    public getItemId = (): string => {
        return this.outlookApi.getItemId();
    };

    public getItemType = (): string => {
        return this.outlookApi.getItemType();
    };

    public getPlatformType = (): PlatformType | undefined => {
        return this.addinApi.getPlatformType();
    };

    public getBodyAsTextAsync = async (): Promise<string> => {
        if (this.getHost() === 'Outlook') return await this.outlookApi.getBodyAsTextAsync();
        else {
            return (await this.getDocumentApi()?.getBodyAsTextAsync()) || '';
        }
    };

    public getBodyAsHtmlAsync = async (): Promise<string> => {
        return await this.outlookApi.getBodyAsHtmlAsync();
    };

    public getBodyTypeAsync = async (): Promise<string> => {
        return await this.outlookApi.getBodyTypeAsync();
    };

    public setBodyAsync = async (newBody: string, bodyType: string): Promise<void> => {
        await this.outlookApi.setBodyAsync(newBody, bodyType);
    };

    public getSubjectAsync = async (): Promise<string> => {
        return await this.outlookApi.getSubjectAsync();
    };

    public setSubjectAsync = async (newSubject: string): Promise<void> => {
        await this.outlookApi.setSubjectAsync(newSubject);
    };

    public getPlatform = (): string => {
        if (this.getHost() === 'Outlook') return this.outlookApi.getPlatform();
        else return this.getDocumentApi()?.getPlatform();
    };

    public getAttachments = async (): Promise<AttachmentOffice[]> => {
        let attachments: AttachmentOffice[] = [];

        if (!this.itemId) {
            await this.saveAsync();
        }
        if (this.itemId) {
            attachments = await this.outlookApi.getAttachmentsAsync(this.itemId);
        }
        return attachments;
    };

    public getSenderAddress = (): string => {
        return this.outlookApi.getSenderAddress();
    };

    public getSenderDisplayName = (): string => {
        return this.outlookApi.getSenderDisplayName();
    };

    public getOriginalItemSender = (): Promise<EmailUser> => {
        return this.getInReplyToAsync().then((inReplyTo) => this.outlookApi.getOriginalItemSender(inReplyTo));
    };

    public showDialogAsync = (url: string, width?: number, height?: number): Promise<Office.Dialog> => {
        return this.outlookApi.showDialogAsync(url, width, height);
    };

    public messageParent = (message: string): void => {
        this.outlookApi.messageParent(message);
    };

    public onItemChanged = (callback: () => void): void => {
        if (this.getHost() === 'Outlook') {
            this.outlookApi.onItemChanged(callback);
        }
    };

    public getOutlookCustomProperties = async (propertyNames: string[]): Promise<Record<string, string>> => {
        return this.outlookApi.getCustomProperties(propertyNames);
    };

    public setOutlookCustomProperties = async (properties: DictionaryInterface<string>): Promise<void> => {
        return this.outlookApi.setCustomProperties(properties);
    };

    public removeOutlookCustomProperties = async (properties: DictionaryInterface<string>): Promise<void> => {
        return this.outlookApi.removeCustomProperties(properties);
    };

    public getProperties = async (): Promise<BuiltInProperties> => {
        return await this.getDocumentApi()?.getProperties();
    };

    public getCustomProperties = (requestedCustomPropertiesNames: string[]): Promise<MetadataObject> | undefined => {
        return this.getDocumentApi()?.getCustomProperties(requestedCustomPropertiesNames);
    };

    public getAllCustomProperties = (): Promise<MetadataObject | undefined> => {
        return this.getDocumentApi()?.getAllCustomProperties();
    };

    public setCustomProperties = async (
        selectedCustomProperties: DictionaryInterface<string | string[]>,
        associatedCustomPropertiesNames: string[],
    ): Promise<void> => {
        await this.getDocumentApi()?.setCustomProperties(selectedCustomProperties, associatedCustomPropertiesNames);
    };

    public getHeaderContent = (): Promise<string> => {
        return this.getDocumentApi()?.getHeaderContent();
    };

    public getFooterContent = (): Promise<string> => {
        return this.getDocumentApi()?.getFooterContent();
    };

    public setHeaderAndFooterAsync = async (
        location: 'header' | 'footer',
        headerFooterText: string,
        titusPrefix: string,
        options?: HeaderFooterOptions,
    ): Promise<void> => {
        const resolvedOptions = options ?? defaultHeaderFooterOptions(titusPrefix);

        if (this.getDocumentApi() === this.wordApi) {
            await this.wordApi.setHeaderAndFooterAsync(
                location,
                headerFooterText,
                resolvedOptions.insertAtStart ? 'start' : 'end',
                resolvedOptions.removeMarkings,
                resolvedOptions.sections,
                resolvedOptions.bookmarkPrefix,
                resolvedOptions.pages,
                resolvedOptions.retainFields,
            );
        } else if (this.getDocumentApi() === this.excelApi) {
            await this.excelApi.setHeaderAndFooterAsync(location, headerFooterText, resolvedOptions);
        } else if (this.getDocumentApi() === this.powerPointApi) {
            await this.powerPointApi.setHeaderAndFooterAsync(location, headerFooterText, resolvedOptions);
        }
    };

    async insertPortionMark(portionMarkStartText: string, portionMarkEndText: string) {
        let coercionType = Office.CoercionType.Html;

        if (this.getDocumentApi() === this.powerPointApi) {
            portionMarkStartText = this.getPlainTextFromHtml(portionMarkStartText);
            portionMarkEndText = this.getPlainTextFromHtml(portionMarkEndText);
            coercionType = Office.CoercionType.Text;
        }

        if (!portionMarkStartText && !portionMarkEndText) {
            return;
        }

        try {
            if (Office.context.document) {
                Office.context.document.getSelectedDataAsync(coercionType, (asyncResult) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                        const selectedText = asyncResult.value;
                        const newText = `${portionMarkStartText}${selectedText}${portionMarkEndText}`;

                        // Set the modified text back to the selection.
                        Office.context.document.setSelectedDataAsync(
                            newText,
                            { coercionType: coercionType },
                            (setResult) => {
                                if (setResult.status === Office.AsyncResultStatus.Succeeded) {
                                    console.log(
                                        'Text inserted successfully at the beginning and end of the selection.',
                                    );
                                } else {
                                    console.error('Error inserting text:', setResult.error);
                                }
                            },
                        );
                    } else {
                        console.error('Error getting selected text:', asyncResult.error);
                    }
                });
            } else {
                console.error('The Document API is not supported in this context.');
            }
        } catch (error) {
            console.error('Error:', error);
        }
    }

    public isItemTypeSupported = (): boolean => {
        return this.outlookApi.isMailBoxItemTypeSupported();
    };

    public getDocumentType = (): DocumentTypeEnum => {
        return this.getDocumentApi()?.getDocumentType();
    };

    public getFileName = (): Promise<string> => {
        return this.getDocumentApi()?.getFileName();
    };

    public getFilePath = (): Promise<string> => {
        return this.getDocumentApi()?.getFilePath();
    };

    private getDocumentApi = (): WordApi | ExcelApi | PowerPointApi => {
        if (!this.documentApi) {
            if (!this.host) this.host = this.getHost();
            if (this.host === 'Word') {
                this.documentApi = this.wordApi;
            } else if (this.host === 'Excel') {
                this.documentApi = this.excelApi;
            } else if (this.host === 'PowerPoint') {
                this.documentApi = this.powerPointApi;
            }
        }
        return this.documentApi as WordApi | ExcelApi | PowerPointApi;
    };

    private getPlainTextFromHtml(html: string): string {
        const parser = new DOMParser();

        // Parse the HTML string into a DOM Document
        const doc = parser.parseFromString(html, 'text/html');

        return doc.body?.textContent || '';
    }
}
